import { readWords } from "@amtana/i18n";
import type { SupportedLanguage } from "@amtana/i18n";

import type { SeoDeclaration, SeoJsonLdBlock } from "./web-module.ts";

// A prerendered page's own <head>, built from a screen's `seo` (web-module.ts) and the few
// values only the render knows: the title already read in a language, the page's own absolute
// address, and the other languages' own addresses (ADR-027 points 2 and 6, epic 31, S31.01 and
// S31.02). Pure: no DOM, no clock, no module. Lane B's prerender (`apps/website`) is the only
// caller outside this file's own tests, so the API stays small: `renderHead`, `renderSitemap`,
// `renderRobots`, and the structured data a screen's `seo.jsonLd` calls: `homeJsonLd`, `faqJsonLd`,
// `breadcrumbJsonLd`, `offerJsonLd`.

/** One language's own address for a page; `renderHead` and `renderSitemap` both read these to
 * write hreflang (plus `x-default`, pointing at the "en" entry, ADR-027 point 5). */
export type HeadAlternate = { readonly language: SupportedLanguage; readonly url: string };

export type SiteSettings = {
  /** No trailing slash: "https://zaistev.com". */
  readonly siteUrl: string;
  readonly productName: string;
  /** The share image every page falls back to when its own `seo.image` is absent; absolute or
   * site-relative ("/share.png"), made absolute the same way `seo.image` is. */
  readonly defaultImage: string;
  /** "@handle"; absent carries no `twitter:site`. */
  readonly twitterSite?: string;
  /** The languages this site is written in; absent means English and Spanish (ADR-035 point 4). */
  readonly languages?: readonly SupportedLanguage[];
};

export type RenderHeadInput = {
  readonly seo: SeoDeclaration;
  /** The screen's own title, already read in `language` (`readWords(screen.title, language)`). */
  readonly title: string;
  /** This page's own absolute address. */
  readonly canonicalUrl: string;
  readonly language: SupportedLanguage;
  /** Every shipped language's own address for this page, English included. */
  readonly alternates: readonly HeadAlternate[];
  readonly site: SiteSettings;
};

/** `path` made absolute against `siteUrl`; already-absolute values pass through unchanged. */
function absoluteUrl(path: string, siteUrl: string): string {
  if (/^https?:\/\//.test(path)) return path;
  return `${siteUrl}${path.startsWith("/") ? path : `/${path}`}`;
}

/** Strict escaping for text inside an HTML attribute or element: every page's words may hold a
 * quote, an ampersand or a stray `</script>`-shaped string, and none of it may become markup. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** A JSON-LD block as a `<script>` body: valid JSON that cannot close its own tag early. */
function escapeJsonLd(block: SeoJsonLdBlock): string {
  return JSON.stringify(block).replace(/</g, "\\u003c").replace(/>/g, "\\u003e").replace(/&/g, "\\u0026");
}

function metaName(name: string, content: string): string {
  return `<meta name="${escapeHtml(name)}" content="${escapeHtml(content)}">`;
}

function metaProperty(property: string, content: string): string {
  return `<meta property="${escapeHtml(property)}" content="${escapeHtml(content)}">`;
}

/**
 * A page's <head> markup: title, description, absolute canonical, hreflang alternates plus
 * `x-default`, Open Graph, Twitter, a `noindex` meta when `seo.noindex`, and one
 * `<script type="application/ld+json">` per `seo.jsonLd(language)` block.
 */
export function renderHead(input: RenderHeadInput): string {
  const { seo, title, canonicalUrl, language, alternates, site } = input;
  const description = readWords(seo.description, language);
  const image = absoluteUrl(seo.image ?? site.defaultImage, site.siteUrl);
  const type = seo.type ?? "website";
  const defaultUrl = alternates.find((alternate) => alternate.language === "en")?.url ?? canonicalUrl;

  const lines = [`<title>${escapeHtml(title)}</title>`, metaName("description", description)];
  if (seo.noindex) lines.push(metaName("robots", "noindex"));
  lines.push(`<link rel="canonical" href="${escapeHtml(canonicalUrl)}">`);
  // A site written in one language has no alternate to name, so it names none (D1264).
  if (site.languages?.length !== 1) {
    for (const alternate of alternates) {
      lines.push(
        `<link rel="alternate" hreflang="${escapeHtml(alternate.language)}" href="${escapeHtml(alternate.url)}">`,
      );
    }
    lines.push(`<link rel="alternate" hreflang="x-default" href="${escapeHtml(defaultUrl)}">`);
  }
  for (const link of seo.alternateLinks?.(language) ?? []) {
    const href = escapeHtml(absoluteUrl(link.href, site.siteUrl));
    lines.push(
      `<link rel="alternate" type="${escapeHtml(link.type)}" title="${escapeHtml(link.title)}" href="${href}">`,
    );
  }

  lines.push(
    metaProperty("og:title", title),
    metaProperty("og:description", description),
    metaProperty("og:image", image),
    metaProperty("og:url", canonicalUrl),
    metaProperty("og:type", type),
    metaProperty("og:site_name", site.productName),
  );

  lines.push(
    metaName("twitter:card", "summary_large_image"),
    metaName("twitter:title", title),
    metaName("twitter:description", description),
    metaName("twitter:image", image),
  );
  if (site.twitterSite) lines.push(metaName("twitter:site", site.twitterSite));

  if (seo.keywords?.length) {
    lines.push(metaName("keywords", seo.keywords.join(", ")));
    if (type === "article") for (const tag of seo.keywords) lines.push(metaProperty("article:tag", tag));
  }

  for (const block of seo.jsonLd?.(language) ?? []) {
    lines.push(`<script type="application/ld+json">${escapeJsonLd(block)}</script>`);
  }

  return lines.join("\n");
}

export type SitemapEntry = {
  readonly url: string;
  readonly alternates: readonly HeadAlternate[];
  /** When the page last changed, a W3C datetime (ADR-028 point 1); absent writes no tag. */
  readonly lastmod?: string;
};

/** `sitemap.xml`: every entry's own address and the date it last changed, with its alternates
 * as `xhtml:link` (plus `x-default`), so a crawler reading one language finds every other from
 * the same file and sees each page's real age. */
export function renderSitemap(entries: readonly SitemapEntry[]): string {
  const urls = entries.map((entry) => {
    const defaultUrl = entry.alternates.find((alternate) => alternate.language === "en")?.url ?? entry.url;
    const links = entry.alternates.map(
      (alternate) =>
        `    <xhtml:link rel="alternate" hreflang="${escapeHtml(alternate.language)}" href="${escapeHtml(alternate.url)}"/>`,
    );
    return [
      "  <url>",
      `    <loc>${escapeHtml(entry.url)}</loc>`,
      ...(entry.lastmod ? [`    <lastmod>${escapeHtml(entry.lastmod)}</lastmod>`] : []),
      ...links,
      ...(links.length > 0
        ? [`    <xhtml:link rel="alternate" hreflang="x-default" href="${escapeHtml(defaultUrl)}"/>`]
        : []),
      "  </url>",
    ].join("\n");
  });
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml">',
    ...urls,
    "</urlset>",
  ].join("\n");
}

/** `robots.txt`: everything open, pointed at the one sitemap. */
export function renderRobots(siteUrl: string): string {
  return ["User-agent: *", "Allow: /", "", `Sitemap: ${absoluteUrl("/sitemap.xml", siteUrl)}`, ""].join("\n");
}

export type HomeJsonLdInput = {
  readonly siteUrl: string;
  readonly productName: string;
  /** The Legal Notice's own company name (legal-entity.ts' `company`), never the product's. */
  readonly legalName: string;
  readonly address: string;
  readonly logo: string;
  /** The SoftwareApplication block's own description; the home page's own `seo.description` fits. */
  readonly description: string;
};

/**
 * The home page's own structured data (ADR-027 point 2): Organization (the Legal Notice's
 * company, never the product's own name, which is a brand, not the entity behind it), WebSite
 * and SoftwareApplication. A screen wires this through its `seo.jsonLd`.
 */
export function homeJsonLd(input: HomeJsonLdInput): readonly SeoJsonLdBlock[] {
  const context = "https://schema.org";
  return [
    {
      "@context": context,
      "@type": "Organization",
      name: input.legalName,
      url: input.siteUrl,
      logo: absoluteUrl(input.logo, input.siteUrl),
      address: input.address,
    },
    {
      "@context": context,
      "@type": "WebSite",
      name: input.productName,
      url: input.siteUrl,
    },
    {
      "@context": context,
      "@type": "SoftwareApplication",
      name: input.productName,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      description: input.description,
      url: input.siteUrl,
    },
  ];
}

export type FaqEntry = { readonly question: string; readonly answer: string };

/** A `FAQPage` block (ADR-034 point 6). Read `entries` from the constant the visible FAQ reads:
 * `pnpm site:audit`'s `json-ld` fails a question the body does not show. */
export function faqJsonLd(entries: readonly FaqEntry[]): SeoJsonLdBlock {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: entries.map((entry) => ({
      "@type": "Question",
      name: entry.question,
      acceptedAnswer: { "@type": "Answer", text: entry.answer },
    })),
  };
}

export type BreadcrumbInput = {
  readonly siteUrl: string;
  /** From the top of the site down to this page, each path already in the page's own language. */
  readonly items: ReadonlyArray<{ readonly name: string; readonly path: string }>;
};

/** A `BreadcrumbList` block (ADR-034 point 7): every family page declares one. */
export function breadcrumbJsonLd(input: BreadcrumbInput): SeoJsonLdBlock {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: input.items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path, input.siteUrl),
    })),
  };
}

export type OfferInput = {
  readonly siteUrl: string;
  readonly name: string;
  readonly path: string;
  /** The price in euro cents, as the billing contract states it. */
  readonly priceCents: number;
};

/** One plan's `Offer` in EUR (ADR-034 point 6), to sit in a `SoftwareApplication`'s `offers`. */
export function offerJsonLd(input: OfferInput): SeoJsonLdBlock {
  return {
    "@type": "Offer",
    name: input.name,
    price: (input.priceCents / 100).toFixed(2),
    priceCurrency: "EUR",
    url: absoluteUrl(input.path, input.siteUrl),
  };
}
